import React, { useContext } from 'react'
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { useNavigate } from 'react-router-dom'
import { UserContext } from '../../App'

export default function UpgradeRequiredCard({
  requiredTier = 3,
  featureName = 'Feature',
  caption,
  benefits = [],
  compact = false,
  areNotIs,
}) {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)

  // const allowedTeams = ['Personnel', 'Fleet', 'Governance', 'Manager']
  const allowedTeams = []
  const requiredTierName = requiredTier <= 2 ? 'Premium' : 'Enterprise'

  const isBillPayer =
    (user?.security?.isSiteAdmin ||
      user?.employee?.teams?.some((x) => allowedTeams.includes(x))) ??
    false

  const defaultBenefits = [
    'Unlock additional workflows',
    'Improve visibility across teams',
    'Reduce duplicated admin',
  ]

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 2 : 2.5,
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid rgba(35,154,164,0.18)',
        background:
          'radial-gradient(circle at top left, rgba(35,154,164,0.16), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 55%, #101827 100%)',
        color: 'white',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.10)',
              color: '#46D7E1',
              flexShrink: 0,
            }}
          >
            <LockRoundedIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={900} lineHeight={1.2} color="white">
              {isBillPayer
                ? `Unlock ${featureName}`
                : `${featureName} unavailable`}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.68)',
                display: 'block',
                mt: 0.3,
              }}
            >
              {caption ||
                (isBillPayer
                  ? `${featureName} ${areNotIs ? 'are' : 'is'} available on the ${requiredTierName} package.`
                  : "Please contact your organisation's administrator")}
            </Typography>
          </Box>
        </Stack>

        {isBillPayer && (
          <>
            <Chip
              size="small"
              label={`${requiredTierName} feature`}
              sx={{
                alignSelf: 'flex-start',
                backgroundColor: 'rgba(255,255,255,0.10)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.16)',
                fontWeight: 800,
              }}
            />

            <Stack spacing={0.8}>
              {(benefits.length ? benefits : defaultBenefits)
                .slice(0, compact ? 2 : 4)
                .map((benefit, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <CheckCircleRoundedIcon
                      sx={{
                        fontSize: 16,
                        color: '#46D7E1',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255,255,255,0.84)',
                        lineHeight: 1.3,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
          </>
        )}

        {isBillPayer && user?.security?.isSiteAdmin && (
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<WorkspacePremiumRoundedIcon />}
            onClick={() => navigate('/site-configuration/billing')}
            sx={{
              mt: 0.5,
              textTransform: 'none',
              fontWeight: 900,
              borderRadius: 2,
              backgroundColor: '#239AA4',
              '&:hover': {
                backgroundColor: '#1D858E',
              },
            }}
          >
            View upgrade options
          </Button>
        )}
      </Stack>
    </Paper>
  )
}
